import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Search,
  Download,
  RefreshCw,
  Inbox,
  ChevronDown,
  Mail,
  Building2,
  Phone,
  Calendar,
  KeyRound,
  X,
  Lock,
  Send,
  CheckCircle2,
  AlertCircle,
  CircleDashed,
  Trash2,
} from "lucide-react";
import Logo from "@/components/Logo";
import SiteContentEditor from "@/pages/admin/SiteContentEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUEST_TYPE_LABELS = {
  buyer_application: "Buyer application",
  supplier_application: "Supplier application",
  buyer_sourcing_support: "Buyer — sourcing support",
  supplier_buyer_access: "Supplier — buyer access",
  tailored_sourcing_report: "Tailored sourcing report",
  specific_opportunity: "Specific opportunity",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const DetailRow = ({ label, value }) =>
  value ? (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-brand-quiet">
        {label}
      </span>
      <span className="text-sm text-brand-warm">{value}</span>
    </div>
  ) : null;

// Normalize legacy rows: no webhook_status field = never attempted = "pending"
const normalizedWebhookStatus = (s) => {
  const raw = s?.webhook_status;
  if (raw === "sent") return "sent";
  if (raw === "failed") return "failed";
  if (raw === "unconfigured") return "unconfigured";
  return "pending";
};

const WEBHOOK_PILL_STYLES = {
  sent: {
    label: "Sent",
    className: "bg-brand-olive/15 text-brand-olive border-brand-olive/30",
    Icon: CheckCircle2,
  },
  failed: {
    label: "Failed",
    className: "bg-red-100 text-red-700 border-red-300",
    Icon: AlertCircle,
  },
  pending: {
    label: "Pending",
    className: "bg-brand-linen text-brand-muted border-brand-border",
    Icon: CircleDashed,
  },
  unconfigured: {
    label: "Not configured",
    className: "bg-brand-linen text-brand-muted border-brand-border",
    Icon: CircleDashed,
  },
};

const WebhookStatusPill = ({ status, testId }) => {
  const spec = WEBHOOK_PILL_STYLES[status] || WEBHOOK_PILL_STYLES.pending;
  const { Icon } = spec;
  return (
    <span
      data-testid={testId}
      title={`Webhook status: ${spec.label}`}
      className={`inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.14em] uppercase px-2.5 py-1 rounded-full border ${spec.className}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2} />
      {spec.label}
    </span>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [totals, setTotals] = useState({ all: 0, buyer: 0, supplier: 0 });
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");
  const [resendingId, setResendingId] = useState(null);
  const [confirmSub, setConfirmSub] = useState(null); // { id, enquiry_id, sent_at } | null
  const [view, setView] = useState("submissions"); // "submissions" | "site"
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearText, setClearText] = useState("");
  const [clearing, setClearing] = useState(false);

  // Verify session server-side (source of truth)
  useEffect(() => {
    if (location.state?.user) {
      setAuthState(true);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
        setAuthState(true);
      } catch (err) {
        const status = err?.response?.status;
        setAuthState(false);
        navigate(status === 403 ? "/admin/login?denied=1" : "/admin/login", {
          replace: true,
        });
      }
    })();
  }, [location.state, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?audience=${filter}`;
      const res = await axios.get(`${API}/admin/intake${q}`, {
        withCredentials: true,
      });
      setSubs(res.data.submissions || []);
      if (res.data.totals) setTotals(res.data.totals);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    if (authState === true) loadData();
  }, [authState, loadData]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* noop */
    }
    navigate("/admin/login", { replace: true });
  };

  const resendWebhook = async (submissionId, { confirm = false } = {}) => {
    if (!submissionId || resendingId) return;
    setResendingId(submissionId);
    try {
      const res = await axios.post(
        `${API}/admin/intake/${submissionId}/resend`,
        confirm ? { confirm: true } : {},
        { withCredentials: true }
      );
      const data = res.data || {};
      setSubs((prev) =>
        prev.map((row) =>
          row.id === submissionId
            ? {
                ...row,
                webhook_status: data.webhook_status,
                webhook_enquiry_id: data.webhook_enquiry_id,
                webhook_error: data.webhook_error,
                webhook_attempted_at: data.webhook_attempted_at,
                resend_count:
                  typeof data.resend_count === "number"
                    ? data.resend_count
                    : row.resend_count,
                last_resend_at: data.last_resend_at || row.last_resend_at,
                resend_history: data.resend_history || row.resend_history,
              }
            : row
        )
      );
      if (data.webhook_status === "sent") {
        toast.success(
          data.was_resend
            ? "Re-sent to ISBackend."
            : "Forwarded to ISBackend."
        );
      } else {
        toast.error(
          data.webhook_error
            ? `Resend failed: ${String(data.webhook_error).slice(0, 120)}`
            : "Resend failed. Please try again."
        );
      }
      setConfirmSub(null);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Resend failed.");
    } finally {
      setResendingId(null);
    }
  };

  const clearInbox = async () => {
    if (clearText.trim() !== "DELETE ALL" || clearing) return;
    setClearing(true);
    try {
      const res = await axios.delete(`${API}/admin/intake`, {
        withCredentials: true,
        data: { confirm_text: "DELETE ALL" },
      });
      const deleted = res.data?.deleted ?? 0;
      toast.success(
        deleted === 0
          ? "Inbox is already empty."
          : `Cleared ${deleted} submission${deleted === 1 ? "" : "s"}.`
      );
      setShowClearModal(false);
      setClearText("");
      await loadData();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Clear failed.");
    } finally {
      setClearing(false);
    }
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    setPwError("");
    if (pwForm.next.length < 8) {
      setPwError("New password must be at least 8 characters.");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      setPwError("New password and confirmation do not match.");
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: pwForm.current, new_password: pwForm.next },
        { withCredentials: true }
      );
      toast.success("Password updated.");
      setShowPw(false);
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setPwError(typeof d === "string" ? d : "Could not update password.");
    } finally {
      setPwSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subs;
    return subs.filter((s) =>
      [
        s.full_name,
        s.company_name,
        s.work_email,
        s.ingredient_category,
        s.geography,
        s.markets_served,
        s.primary_goal,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [subs, search]);

  const exportCsv = () => {
    const cols = [
      "submitted_at",
      "audience",
      "request_type",
      "full_name",
      "role",
      "company_name",
      "work_email",
      "phone",
      "ingredient_category",
      "ingredient_form",
      "application_area",
      "geography",
      "markets_served",
      "volume_range",
      "capacity_range",
      "moq",
      "certifications",
      "timeline",
      "primary_goal",
      "program_interest",
      "current_challenge",
      "additional_notes",
      "webhook_status",
      "webhook_enquiry_id",
      "webhook_attempted_at",
      "resend_count",
      "last_resend_at",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(",")].concat(
      filtered.map((s) => cols.map((c) => esc(s[c])).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ingredientsignal-submissions-${filter}-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authState === null) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <span className="text-brand-muted font-display">Verifying access…</span>
      </div>
    );
  }
  if (authState === false) return null;

  const TABS = [
    { key: "all", label: "All", count: totals.all },
    { key: "buyer", label: "Buyers", count: totals.buyer },
    { key: "supplier", label: "Suppliers", count: totals.supplier },
  ];

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-brand-cream/90 backdrop-blur-xl border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden sm:inline text-[11px] font-semibold tracking-[0.2em] uppercase text-brand-olive border-l border-brand-border pl-4">
              Admin Inbox
            </span>
          </div>
          <div className="flex items-center gap-4">
            {user?.email && (
              <span className="hidden md:inline text-sm text-brand-muted" data-testid="admin-user-email">
                {user.email}
              </span>
            )}
            <button
              type="button"
              data-testid="admin-change-password"
              onClick={() => { setPwError(""); setShowPw(true); }}
              className="inline-flex items-center gap-2 border border-brand-border hover:border-brand-olive/50 text-brand-deep px-4 py-2 rounded-full text-sm transition-colors hover:bg-brand-ivory"
            >
              <KeyRound className="w-4 h-4" />
              <span className="hidden sm:inline">Password</span>
            </button>
            <button
              type="button"
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 border border-brand-border hover:border-brand-olive/50 text-brand-deep px-4 py-2 rounded-full text-sm transition-colors hover:bg-brand-ivory"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* View switcher */}
        <div
          className="inline-flex border border-brand-border bg-brand-ivory rounded-full p-1 mb-8"
          data-testid="admin-view-switcher"
        >
          {[
            { key: "submissions", label: "Submissions" },
            { key: "site", label: "Site Content" },
          ].map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setView(v.key)}
              data-testid={`admin-view-${v.key}`}
              className={`px-5 py-2 rounded-full text-xs font-semibold tracking-[0.14em] uppercase transition-colors ${
                view === v.key
                  ? "bg-brand-olive text-brand-cream"
                  : "text-brand-muted hover:text-brand-deep"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {view === "site" ? (
          <SiteContentEditor />
        ) : (
        <>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-brand-deep font-light">
              Submissions
            </h1>
            <p className="mt-2 text-brand-muted text-sm">
              Buyer &amp; supplier requests from the Get Started form.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadData}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-brand-border hover:border-brand-olive/50 text-brand-deep px-4 py-2.5 rounded-full text-sm transition-colors hover:bg-brand-ivory"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={exportCsv}
              data-testid="admin-export-csv"
              disabled={filtered.length === 0}
              className="inline-flex items-center gap-2 bg-brand-olive hover:bg-brand-hover text-brand-cream px-4 py-2.5 rounded-full text-sm transition-colors disabled:opacity-40"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              type="button"
              onClick={() => {
                setClearText("");
                setShowClearModal(true);
              }}
              disabled={subs.length === 0}
              data-testid="admin-clear-inbox-open"
              title={
                subs.length === 0
                  ? "Inbox is already empty"
                  : "Permanently delete every submission in the inbox"
              }
              className="inline-flex items-center gap-2 border border-red-300/70 text-red-700 hover:bg-red-50 hover:border-red-500 px-4 py-2.5 rounded-full text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              Clear inbox
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="inline-flex border border-brand-border bg-brand-ivory rounded-full p-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setFilter(t.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filter === t.key
                    ? "bg-brand-olive text-brand-cream"
                    : "text-brand-muted hover:text-brand-deep"
                }`}
              >
                {t.label}
                <span
                  className={`text-xs tabular-nums ${
                    filter === t.key ? "text-brand-cream/80" : "text-brand-quiet"
                  }`}
                >
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          <div className="relative md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-quiet" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, company, email, ingredient…"
              data-testid="admin-search"
              className="w-full bg-brand-ivory border border-brand-border rounded-full pl-10 pr-4 py-2.5 text-sm text-brand-deep placeholder:text-brand-quiet/70 focus:outline-none focus:border-brand-olive focus:ring-1 focus:ring-brand-olive transition-colors"
            />
          </div>
        </div>

        {/* List */}
        <div className="mt-8">
          {loading ? (
            <div className="py-24 text-center text-brand-muted">Loading…</div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="admin-empty-state"
              className="py-24 text-center border border-dashed border-brand-border rounded-2xl bg-brand-ivory"
            >
              <Inbox className="w-8 h-8 text-brand-quiet mx-auto" strokeWidth={1.4} />
              <p className="mt-4 text-brand-muted">
                {search ? "No submissions match your search." : "No submissions yet."}
              </p>
            </div>
          ) : (
            <div className="border border-brand-border rounded-2xl overflow-hidden bg-brand-ivory divide-y divide-brand-border">
              {filtered.map((s, idx) => {
                const isOpen = expanded === (s.id || idx);
                const key = s.id || idx;
                const wStatus = normalizedWebhookStatus(s);
                const resendCount = Number(s.resend_count || 0);
                const isSent = wStatus === "sent";
                const canPrimaryResend =
                  Boolean(s.id) && (wStatus === "failed" || wStatus === "pending");
                const canConfirmResend = Boolean(s.id) && isSent;
                const isResendingRow = resendingId === s.id;
                return (
                  <div key={key} data-testid={`admin-row-${idx}`}>
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : key)}
                      className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-brand-linen/60 transition-colors"
                    >
                      <span
                        className={`text-[10px] font-semibold tracking-[0.14em] uppercase px-2.5 py-1 rounded-full flex-shrink-0 ${
                          s.audience === "supplier"
                            ? "bg-brand-accent/15 text-brand-olive"
                            : "bg-brand-olive/15 text-brand-deep"
                        }`}
                      >
                        {s.audience || "—"}
                      </span>
                      <WebhookStatusPill
                        status={wStatus}
                        testId={`admin-webhook-pill-${idx}`}
                      />
                      {resendCount > 0 && (
                        <span
                          data-testid={`admin-resent-chip-${idx}`}
                          title={`Manually re-sent ${resendCount}× · last on ${fmtDate(
                            s.last_resend_at
                          )}`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.14em] uppercase px-2 py-1 rounded-full border border-brand-olive/40 bg-brand-linen text-brand-olive"
                        >
                          Re-sent {resendCount}×
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-brand-deep truncate">
                            {s.full_name || "Unknown"}
                          </span>
                          <span className="text-brand-quiet">·</span>
                          <span className="text-sm text-brand-muted truncate">
                            {s.company_name || "—"}
                          </span>
                        </div>
                        <div className="text-xs text-brand-quiet truncate mt-0.5">
                          {s.ingredient_category || REQUEST_TYPE_LABELS[s.request_type] || "—"}
                          {s.geography ? ` · ${s.geography}` : ""}
                          {s.markets_served ? ` · ${s.markets_served}` : ""}
                        </div>
                      </div>
                      <span className="hidden sm:block text-xs text-brand-quiet whitespace-nowrap">
                        {fmtDate(s.submitted_at)}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 text-brand-quiet flex-shrink-0 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isOpen && (
                      <div
                        data-testid={`admin-detail-${idx}`}
                        className="px-5 pb-6 pt-1 bg-brand-cream/60"
                      >
                        <div className="flex flex-wrap gap-4 mb-5">
                          <a
                            href={`mailto:${s.work_email}`}
                            className="inline-flex items-center gap-2 text-sm text-brand-olive hover:text-brand-hover"
                          >
                            <Mail className="w-4 h-4" /> {s.work_email}
                          </a>
                          {s.phone && (
                            <span className="inline-flex items-center gap-2 text-sm text-brand-muted">
                              <Phone className="w-4 h-4" /> {s.phone}
                            </span>
                          )}
                          {s.company_name && (
                            <span className="inline-flex items-center gap-2 text-sm text-brand-muted">
                              <Building2 className="w-4 h-4" /> {s.company_name}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-2 text-sm text-brand-muted">
                            <Calendar className="w-4 h-4" /> {fmtDate(s.submitted_at)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          <DetailRow label="Role" value={s.role} />
                          <DetailRow
                            label="Request"
                            value={REQUEST_TYPE_LABELS[s.request_type] || s.request_type}
                          />
                          <DetailRow label="Ingredient / Category" value={s.ingredient_category} />
                          <DetailRow label="Form" value={s.ingredient_form} />
                          <DetailRow label="Application" value={s.application_area} />
                          <DetailRow label="Geography" value={s.geography} />
                          <DetailRow label="Markets served" value={s.markets_served} />
                          <DetailRow label="Volume" value={s.volume_range} />
                          <DetailRow label="Capacity" value={s.capacity_range} />
                          <DetailRow label="MOQ" value={s.moq} />
                          <DetailRow label="Certifications" value={s.certifications} />
                          <DetailRow label="Timeline" value={s.timeline} />
                          <DetailRow label="Primary goal" value={s.primary_goal} />
                          <DetailRow label="Program interest" value={s.program_interest} />
                        </div>
                        {(s.current_challenge || s.additional_notes) && (
                          <div className="mt-5 pt-5 border-t border-brand-border">
                            <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-brand-quiet">
                              Notes
                            </span>
                            <p className="mt-2 text-sm text-brand-warm leading-relaxed whitespace-pre-wrap">
                              {s.current_challenge || s.additional_notes}
                            </p>
                          </div>
                        )}

                        {/* ISBackend delivery status + resend */}
                        <div
                          data-testid={`admin-webhook-block-${idx}`}
                          className="mt-5 pt-5 border-t border-brand-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-brand-quiet">
                                ISBackend delivery
                              </span>
                              <WebhookStatusPill
                                status={wStatus}
                                testId={`admin-webhook-detail-pill-${idx}`}
                              />
                            </div>
                            <div className="mt-2 text-xs text-brand-muted space-y-0.5">
                              {s.webhook_attempted_at && (
                                <div>
                                  Last attempt:{" "}
                                  <span className="text-brand-warm">
                                    {fmtDate(s.webhook_attempted_at)}
                                  </span>
                                </div>
                              )}
                              {s.webhook_enquiry_id && (
                                <div className="font-mono truncate">
                                  enquiry_id:{" "}
                                  <span className="text-brand-warm">
                                    {s.webhook_enquiry_id}
                                  </span>
                                </div>
                              )}
                              {wStatus === "failed" && s.webhook_error && (
                                <div className="text-red-700 truncate max-w-md">
                                  {String(s.webhook_error).slice(0, 200)}
                                </div>
                              )}
                              {wStatus === "unconfigured" && (
                                <div className="text-brand-quiet">
                                  Webhook not configured on this environment.
                                </div>
                              )}
                            </div>
                          </div>

                          {canPrimaryResend && (
                            <button
                              type="button"
                              onClick={() => resendWebhook(s.id)}
                              disabled={isResendingRow}
                              data-testid={`admin-webhook-resend-${idx}`}
                              className="inline-flex items-center justify-center gap-2 bg-brand-olive hover:bg-brand-hover text-brand-cream px-4 py-2 rounded-full text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                            >
                              <Send
                                className={`w-3.5 h-3.5 ${
                                  isResendingRow ? "animate-pulse" : ""
                                }`}
                                strokeWidth={1.8}
                              />
                              {isResendingRow ? "Resending…" : "Resend to ISBackend"}
                            </button>
                          )}
                          {canConfirmResend && (
                            <button
                              type="button"
                              onClick={() =>
                                setConfirmSub({
                                  id: s.id,
                                  enquiry_id: s.webhook_enquiry_id,
                                  sent_at:
                                    s.last_resend_at || s.webhook_attempted_at,
                                  resend_count: resendCount,
                                })
                              }
                              disabled={isResendingRow}
                              data-testid={`admin-webhook-resend-sent-${idx}`}
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-muted hover:text-brand-olive underline underline-offset-4 decoration-brand-border hover:decoration-brand-olive transition-colors disabled:opacity-50 flex-shrink-0"
                            >
                              <Send className="w-3 h-3" strokeWidth={1.8} />
                              Resend to ISBackend
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </>
        )}
      </main>

      {showClearModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-deep/50 backdrop-blur-sm"
          data-testid="admin-clear-inbox-modal"
          onClick={() => !clearing && setShowClearModal(false)}
        >
          <div
            className="w-full max-w-md bg-brand-cream border border-red-200 rounded-2xl shadow-[0_30px_80px_-20px_rgba(153,27,27,0.35)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-red-100">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50 text-red-700 border border-red-200">
                  <Trash2 className="w-4 h-4" strokeWidth={1.7} />
                </span>
                <h2 className="font-display text-lg text-brand-deep">
                  Clear the entire inbox?
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowClearModal(false)}
                disabled={clearing}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-brand-muted hover:text-brand-deep hover:bg-brand-linen transition-colors disabled:opacity-50"
                aria-label="Close"
                data-testid="admin-clear-inbox-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-brand-warm leading-relaxed">
                This will permanently delete all{" "}
                <span className="font-semibold text-brand-deep">
                  {subs.length}
                </span>{" "}
                submission{subs.length === 1 ? "" : "s"} — buyers and suppliers
                — from this environment. There is no undo. Downstream ISBackend
                records are NOT affected.
              </p>
              <div>
                <label
                  htmlFor="clear-inbox-input"
                  className="text-[10px] font-semibold tracking-[0.16em] uppercase text-brand-quiet"
                >
                  Type{" "}
                  <span className="font-mono text-red-700 not-italic">
                    DELETE ALL
                  </span>{" "}
                  to enable the button
                </label>
                <input
                  id="clear-inbox-input"
                  autoFocus
                  value={clearText}
                  onChange={(e) => setClearText(e.target.value)}
                  disabled={clearing}
                  data-testid="admin-clear-inbox-input"
                  className="mt-1.5 w-full bg-brand-ivory border border-brand-border rounded-md px-3 py-2 text-sm text-brand-deep placeholder:text-brand-quiet/60 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-colors font-mono"
                  placeholder="DELETE ALL"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowClearModal(false)}
                  disabled={clearing}
                  data-testid="admin-clear-inbox-cancel"
                  className="inline-flex items-center gap-2 border border-brand-border hover:border-brand-muted/60 text-brand-warm px-5 py-2 rounded-full text-sm transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={clearInbox}
                  disabled={clearing || clearText.trim() !== "DELETE ALL"}
                  data-testid="admin-clear-inbox-submit"
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-full text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                  {clearing ? "Clearing…" : "Delete all"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmSub && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-deep/40 backdrop-blur-sm"
          data-testid="admin-resend-confirm-modal"
          onClick={() => resendingId === null && setConfirmSub(null)}
        >
          <div
            className="w-full max-w-md bg-brand-cream border border-brand-border rounded-2xl shadow-[0_30px_80px_-20px_rgba(47,61,30,0.4)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-brand-border">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-linen text-brand-olive border border-brand-olive/30">
                  <AlertCircle className="w-4 h-4" strokeWidth={1.7} />
                </span>
                <h2 className="font-display text-lg text-brand-deep">
                  Re-send to ISBackend?
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setConfirmSub(null)}
                disabled={resendingId !== null}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-brand-muted hover:text-brand-deep hover:bg-brand-linen transition-colors disabled:opacity-50"
                aria-label="Close"
                data-testid="admin-resend-confirm-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p
                className="text-sm text-brand-warm leading-relaxed"
                data-testid="admin-resend-confirm-body"
              >
                This enquiry was already delivered to ISBackend
                {confirmSub.enquiry_id ? (
                  <>
                    {" "}
                    (enquiry_id:{" "}
                    <span className="font-mono text-brand-deep">
                      {confirmSub.enquiry_id}
                    </span>
                    )
                  </>
                ) : null}
                {confirmSub.sent_at ? (
                  <>
                    {" "}
                    on{" "}
                    <span className="text-brand-deep">
                      {fmtDate(confirmSub.sent_at)}
                    </span>
                  </>
                ) : null}
                . Resending may create a duplicate if the original still exists.
                Resend anyway?
              </p>
              {confirmSub.resend_count > 0 && (
                <p className="text-xs text-brand-quiet">
                  This submission has already been manually re-sent{" "}
                  {confirmSub.resend_count}× before.
                </p>
              )}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setConfirmSub(null)}
                  disabled={resendingId !== null}
                  data-testid="admin-resend-confirm-cancel"
                  className="inline-flex items-center gap-2 border border-brand-border hover:border-brand-muted/60 text-brand-warm px-5 py-2 rounded-full text-sm transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() =>
                    resendWebhook(confirmSub.id, { confirm: true })
                  }
                  disabled={resendingId !== null}
                  data-testid="admin-resend-confirm-submit"
                  className="inline-flex items-center gap-2 bg-brand-olive hover:bg-brand-hover text-brand-cream px-5 py-2 rounded-full text-sm font-medium transition-colors disabled:opacity-50"
                >
                  <Send
                    className={`w-3.5 h-3.5 ${
                      resendingId === confirmSub.id ? "animate-pulse" : ""
                    }`}
                    strokeWidth={1.8}
                  />
                  {resendingId === confirmSub.id
                    ? "Re-sending…"
                    : "Resend Anyway"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPw && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-deep/40 backdrop-blur-sm"
          data-testid="admin-password-modal"
          onClick={() => !pwSaving && setShowPw(false)}
        >
          <div
            className="w-full max-w-md bg-brand-cream border border-brand-border rounded-2xl shadow-[0_30px_80px_-20px_rgba(47,61,30,0.4)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-brand-border">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-olive text-brand-cream">
                  <KeyRound className="w-4 h-4" strokeWidth={1.7} />
                </span>
                <h2 className="font-display text-lg text-brand-deep">Change password</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowPw(false)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-brand-muted hover:text-brand-deep hover:bg-brand-linen transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={submitPasswordChange} className="p-6 space-y-4" data-testid="admin-password-form">
              {pwError && (
                <div data-testid="admin-password-error" className="text-sm text-red-700 bg-red-50 border border-red-300 rounded-lg px-3 py-2">
                  {pwError}
                </div>
              )}
              {[
                ["current", "Current password", "current-password"],
                ["next", "New password", "new-password"],
                ["confirm", "Confirm new password", "new-password"],
              ].map(([key, label, ac]) => (
                <div key={key} className="flex flex-col gap-2">
                  <label className="text-xs font-semibold tracking-[0.16em] uppercase text-brand-muted">
                    {label}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-quiet" />
                    <input
                      type="password"
                      autoComplete={ac}
                      value={pwForm[key]}
                      onChange={(e) => setPwForm((f) => ({ ...f, [key]: e.target.value }))}
                      data-testid={`admin-password-${key}`}
                      className="w-full bg-brand-ivory border border-brand-border rounded-xl pl-10 pr-4 py-2.5 text-sm text-brand-deep focus:outline-none focus:border-brand-olive focus:ring-1 focus:ring-brand-olive transition-colors"
                    />
                  </div>
                </div>
              ))}
              <button
                type="submit"
                disabled={pwSaving}
                data-testid="admin-password-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-olive hover:bg-brand-hover text-brand-cream px-6 py-3 rounded-full text-sm font-medium transition-colors disabled:opacity-50 mt-2"
              >
                {pwSaving ? "Saving…" : "Update password"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
